// client/src/pages/admin/AdminProducts.js
import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/common/Navbar';

import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../services/api';
import axios from 'axios';
import { Package, Search, Trash2, Eye, EyeOff, Edit3, X, Upload, Image, ChevronLeft, ChevronRight, Save, Download, CheckSquare, Square } from 'lucide-react';
import { adminAPI } from '../../services/api';
import { getImageUrl } from '../../utils/imageHelper';
import useRangeSelect from '../../hooks/useRangeSelect';
import DragSelectOverlay from '../../components/common/DragSelectOverlay';
import FloatingActionBar from '../../components/common/FloatingActionBar';
import ConfirmModal from '../../components/common/ConfirmModal';

// ================================================================
// EDIT MODAL
// ================================================================
const EditProductModal = ({ product, onClose, onSave }) => {
  const [form, setForm] = useState({
    name: product.name || '',
    description: product.description || '',
    category: product.category || '',
    brand: product.brand || '',
    sku: product.sku || '',
    unitPrice: product.pricing?.unitPrice || 0,
    unit: product.pricing?.unit || 'kg',
    minOrderQuantity: product.pricing?.minOrderQuantity || 1,
    stockStatus: product.stockStatus || 'in_stock',
    status: product.status || 'active',
    origin: product.specifications?.origin || '',
    weight: product.specifications?.weight || '',
    shelfLife: product.specifications?.shelfLife || '',
  });
  const [images, setImages] = useState(product.images || []);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmRemoveImage, setConfirmRemoveImage] = useState(null);

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/admin/platform/products/${product._id}`, {
        name: form.name,
        description: form.description,
        category: form.category,
        brand: form.brand,
        sku: form.sku,
        pricing: { unitPrice: Number(form.unitPrice), unit: form.unit, minOrderQuantity: Number(form.minOrderQuantity) },
        stockStatus: form.stockStatus,
        status: form.status,
        specifications: { origin: form.origin, weight: form.weight, shelfLife: form.shelfLife }
      }, { headers: { Authorization: `Bearer ${token}` } });
      onSave();
    } catch (err) {
      toast.error('Failed to save: ' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('image', file);
      const res = await axios.post(`${API_URL}/admin/platform/products/${product._id}/image`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setImages(res.data.images);
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = async (url) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.delete(`${API_URL}/admin/platform/products/${product._id}/image`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { imageUrl: url }
      });
      setImages(res.data.images);
    } catch (err) {
      toast.error('Failed to remove image');
    }
  };

  const units = ['kg', 'τεμ', 'λίτρο', 'κιβώτιο', 'συσκευασία', 'piece', 'case', 'box'];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="fixed inset-0 bg-black/50" />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between z-10 rounded-t-xl">
          <h2 className="text-lg font-bold text-gray-900">Edit Product</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* Images */}
          <div>
            <label className="text-xs font-medium text-gray-500 block mb-2">Images</label>
            <div className="flex flex-wrap gap-3">
              {images.map((img, i) => (
                <div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden border group">
                  <img src={getImageUrl(img)} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => setConfirmRemoveImage(img)}
                    className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                    <Trash2 className="w-4 h-4 text-white" />
                  </button>
                </div>
              ))}
              <label className="w-20 h-20 rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors">
                {uploading ? <LoadingSpinner size="sm" /> : <>
                  <Upload className="w-4 h-4 text-gray-400" />
                  <span className="text-[10px] text-gray-400 mt-1">Add</span>
                </>}
                <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} disabled={uploading} />
              </label>
            </div>
          </div>

          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="text-xs font-medium text-gray-500 block mb-1">Product Name *</label>
              <input value={form.name} onChange={e => handleChange('name', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-medium text-gray-500 block mb-1">Description</label>
              <textarea value={form.description} onChange={e => handleChange('description', e.target.value)}
                rows={2} className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 resize-none" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 block mb-1">Category *</label>
              <input value={form.category} onChange={e => handleChange('category', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 block mb-1">Brand</label>
              <input value={form.brand} onChange={e => handleChange('brand', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 block mb-1">SKU</label>
              <input value={form.sku} onChange={e => handleChange('sku', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 block mb-1">Status</label>
              <select value={form.status} onChange={e => handleChange('status', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm">
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          {/* Pricing */}
          <div>
            <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-3">Pricing</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-xs font-medium text-gray-500 block mb-1">Unit Price (€) *</label>
                <input type="number" step="0.01" min="0" value={form.unitPrice}
                  onChange={e => handleChange('unitPrice', e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 block mb-1">Unit *</label>
                <select value={form.unit} onChange={e => handleChange('unit', e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm">
                  {units.map(u => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 block mb-1">Min Order Qty</label>
                <input type="number" min="1" value={form.minOrderQuantity}
                  onChange={e => handleChange('minOrderQuantity', e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>
          </div>

          {/* Stock */}
          <div>
            <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-3">Stock Status</h3>
            <div className="flex gap-2">
              {['in_stock', 'low_stock', 'out_of_stock'].map(s => (
                <button key={s} onClick={() => handleChange('stockStatus', s)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                    form.stockStatus === s
                      ? s === 'in_stock' ? 'bg-green-100 border-green-300 text-green-700'
                        : s === 'low_stock' ? 'bg-yellow-100 border-yellow-300 text-yellow-700'
                        : 'bg-red-100 border-red-300 text-red-700'
                      : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}>
                  {s.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </button>
              ))}
            </div>
          </div>

          {/* Specifications */}
          <div>
            <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-3">Specifications</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-xs font-medium text-gray-500 block mb-1">Origin</label>
                <input value={form.origin} onChange={e => handleChange('origin', e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 block mb-1">Weight</label>
                <input value={form.weight} onChange={e => handleChange('weight', e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 block mb-1">Shelf Life</label>
                <input value={form.shelfLife} onChange={e => handleChange('shelfLife', e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>
          </div>

          {/* Wholesaler (read-only) */}
          <div className="bg-gray-50 rounded-lg p-3">
            <span className="text-xs text-gray-400">Wholesaler:</span>
            <span className="text-sm font-medium text-gray-700 ml-2">{product.wholesalerId?.companyName || 'Unknown'}</span>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex justify-end gap-3 rounded-b-xl">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
          <button onClick={handleSave} disabled={saving || !form.name || !form.category}
            className="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
            <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Confirm remove image modal */}
      <ConfirmModal
        isOpen={!!confirmRemoveImage}
        title="Remove Image"
        message="Remove this image from the product?"
        icon="warning"
        confirmLabel="Remove"
        confirmColor="red"
        onConfirm={() => { handleRemoveImage(confirmRemoveImage); setConfirmRemoveImage(null); }}
        onCancel={() => setConfirmRemoveImage(null)}
      />
    </div>
  );
};


// ================================================================
// MAIN PAGE
// ================================================================
const AdminProducts = () => {
  const { user } = useAuth();
  const { t } = useTranslation('products');
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [category, setCategory] = useState('all');
  const [categories, setCategories] = useState([]);
  const [wholesalerId, setWholesalerId] = useState('');
  const [wholesalers, setWholesalers] = useState([]);
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [editProduct, setEditProduct] = useState(null);
  const [bulkAction, setBulkAction] = useState(null);

  // Confirm modal state
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', icon: 'warning', confirmLabel: 'Confirm', confirmColor: 'red', onConfirm: () => {} });
  const openConfirm = (opts) => setConfirmModal({ isOpen: true, ...opts });
  const closeConfirm = () => setConfirmModal(prev => ({ ...prev, isOpen: false }));

  // Use shared selection hook
  const {
    selectedIds, setSelectedIds, handleItemClick, toggleSelect,
    selectAll, clearSelection, dragProps, isDragging, dragRect, dragSelectedIds
  } = useRangeSelect(products, '_id');

  useEffect(() => {
    if (!user || user.role !== 'admin') navigate('/admin/login');
  }, [user, navigate]);

  // Fetch wholesalers list for dropdown
  useEffect(() => {
    const fetchWholesalers = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API_URL}/admin/platform/wholesalers-list`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setWholesalers(res.data.wholesalers || []);
      } catch (err) { console.error(err); }
    };
    fetchWholesalers();
  }, []);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ page, limit: 50 });
      if (searchTerm) params.append('search', searchTerm);
      if (category !== 'all') params.append('category', category);
      if (wholesalerId) params.append('wholesalerId', wholesalerId);
      if (status) params.append('status', status);

      const res = await axios.get(`${API_URL}/admin/platform/products?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(res.data.products);
      setTotalPages(res.data.pages);
      setTotal(res.data.total);
      if (res.data.categories) setCategories(res.data.categories);
    } catch (err) {
      console.error('Fetch products error:', err);
    } finally {
      setLoading(false);
    }
  }, [page, searchTerm, category, wholesalerId, status]);

  const fetchStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/admin/platform/products/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(res.data.stats);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  const handleDelete = async (id, name) => {
    openConfirm({
      title: 'Delete Product',
      message: `Delete product "${name}"? This cannot be undone.`,
      icon: 'danger',
      confirmLabel: 'Delete',
      confirmColor: 'red',
      onConfirm: async () => {
        closeConfirm();
        try {
          const token = localStorage.getItem('token');
          await axios.delete(`${API_URL}/admin/platform/products/${id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          fetchProducts();
          fetchStats();
        } catch (err) { toast.error('Failed to delete product'); }
      }
    });
  };

  const handleToggleStock = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/admin/platform/products/${id}/toggle-stock`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchProducts();
    } catch (err) { toast.error('Failed to toggle stock'); }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchTerm(search);
    setPage(1);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === products.length) {
      clearSelection();
    } else {
      selectAll();
    }
  };

  const handleBulkDelete = async () => {
    openConfirm({
      title: 'Delete Selected Products',
      message: `Permanently delete ${selectedIds.size} product${selectedIds.size > 1 ? 's' : ''}? This cannot be undone.`,
      icon: 'danger',
      confirmLabel: 'Delete',
      confirmColor: 'red',
      onConfirm: async () => {
        closeConfirm();
        try {
          await adminAPI.bulkDeleteProducts([...selectedIds]);
          clearSelection();
          fetchProducts();
          fetchStats();
        } catch { toast.error('Bulk delete failed'); }
      }
    });
  };

  const handleDeleteAll = async () => {
    if (!wholesalerId) return toast.error('Please select a wholesaler first');
    const w = wholesalers.find(w => w._id === wholesalerId);
    openConfirm({
      title: 'Delete ALL Products',
      message: `This will permanently remove ALL products for "${w?.companyName || wholesalerId}". This action cannot be undone!`,
      icon: 'danger',
      confirmLabel: 'Delete All',
      confirmColor: 'red',
      onConfirm: async () => {
        closeConfirm();
        try {
          const res = await adminAPI.deleteAllProducts(wholesalerId);
          toast.success(`Deleted ${res.data.deleted} products`);
          clearSelection();
          fetchProducts();
          fetchStats();
        } catch { toast.error('Delete all failed'); }
      }
    });
  };

  const handleBulkStock = async (inStock) => {
    try {
      await adminAPI.bulkToggleStock([...selectedIds], inStock);
      clearSelection();
      fetchProducts();
      fetchStats();
    } catch { toast.error('Bulk stock toggle failed'); }
  };

  const handleBulkCategory = async (newCat) => {
    if (!newCat) return;
    try {
      await adminAPI.bulkChangeCategory([...selectedIds], newCat);
      clearSelection();
      setBulkAction(null);
      fetchProducts();
    } catch { toast.error('Bulk category change failed'); }
  };

  const handleExportProducts = async (format) => {
    if (!wholesalerId) return toast.error('Please select a wholesaler first');
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/admin/platform/products/export/${wholesalerId}?format=${format}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const selected = wholesalers.find(w => w._id === wholesalerId);
      const name = selected?.companyName?.replace(/[^a-zA-Z0-9]/g, '_') || 'products';
      link.download = `${name}_products.${format}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error('Export failed'); }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="pt-16 lg:pt-0">
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Package className="w-6 h-6 text-amber-500" /> Product Management
              </h1>
              <p className="text-sm text-gray-500 mt-1">Browse, edit, and moderate all products across wholesalers</p>
            </div>
          </div>

          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
              <div className="bg-white rounded-lg border p-4">
                <p className="text-xs text-gray-500">Total Products</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
              <div className="bg-white rounded-lg border p-4">
                <p className="text-xs text-gray-500">In Stock</p>
                <p className="text-2xl font-bold text-green-600">{stats.inStock}</p>
              </div>
              <div className="bg-white rounded-lg border p-4">
                <p className="text-xs text-gray-500">Low Stock</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.lowStock || 0}</p>
              </div>
              <div className="bg-white rounded-lg border p-4">
                <p className="text-xs text-gray-500">Out of Stock</p>
                <p className="text-2xl font-bold text-red-600">{stats.outOfStock}</p>
              </div>
              <div className="bg-white rounded-lg border p-4">
                <p className="text-xs text-gray-500">Categories</p>
                <p className="text-2xl font-bold text-blue-600">{categories.length}</p>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="bg-white rounded-lg border p-4 mb-6">
            <div className="flex flex-wrap gap-3 items-end">
              <form onSubmit={handleSearch} className="flex-1 min-w-[200px]">
                <label className="text-xs text-gray-500 block mb-1">Search</label>
                <div className="flex gap-2">
                  <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                    placeholder="Name, SKU, description..."
                    className="flex-1 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
                  <button type="submit" className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                    <Search className="w-4 h-4" />
                  </button>
                </div>
              </form>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Wholesaler</label>
                <select value={wholesalerId} onChange={(e) => { setWholesalerId(e.target.value); setPage(1); }}
                  className="px-3 py-2 border rounded-lg text-sm min-w-[180px]">
                  <option value="">All Wholesalers</option>
                  {wholesalers.map(w => <option key={w._id} value={w._id}>{w.companyName}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Category</label>
                <select value={category} onChange={(e) => { setCategory(e.target.value); setPage(1); }}
                  className="px-3 py-2 border rounded-lg text-sm">
                  <option value="all">All Categories</option>
                  {categories.map(c => <option key={c} value={c}>{t(`categories.${c}`, c?.replace(/_/g, ' '))}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Stock</label>
                <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}
                  className="px-3 py-2 border rounded-lg text-sm">
                  <option value="">All</option>
                  <option value="in_stock">In Stock</option>
                  <option value="low_stock">Low Stock</option>
                  <option value="out_of_stock">Out of Stock</option>
                </select>
              </div>
              {(searchTerm || wholesalerId || category !== 'all' || status) && (
                <button onClick={() => { setSearch(''); setSearchTerm(''); setWholesalerId(''); setCategory('all'); setStatus(''); setPage(1); }}
                  className="px-3 py-2 text-sm text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg border">
                  Clear All
                </button>
              )}
              {wholesalerId && (
                <div className="flex items-center gap-1 ml-auto">
                  <button onClick={() => handleExportProducts('csv')}
                    className="px-3 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-1">
                    <Download className="w-4 h-4" /> CSV
                  </button>
                  <button onClick={() => handleExportProducts('xlsx')}
                    className="px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-1">
                    <Download className="w-4 h-4" /> XLSX
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Bulk Actions Bar (inline, visible when items selected) */}
          {selectedIds.size > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex flex-wrap items-center gap-3">
              <span className="text-sm font-semibold text-blue-800">{selectedIds.size} product{selectedIds.size > 1 ? 's' : ''} selected</span>
              <div className="w-px h-6 bg-blue-200" />
              <button onClick={handleBulkDelete}
                className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-sm font-medium flex items-center gap-1 hover:bg-red-700">
                <Trash2 className="w-3.5 h-3.5" /> Delete Selected
              </button>
              <button onClick={() => handleBulkStock(true)}
                className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm font-medium flex items-center gap-1 hover:bg-green-700">
                <Eye className="w-3.5 h-3.5" /> Set In Stock
              </button>
              <button onClick={() => handleBulkStock(false)}
                className="px-3 py-1.5 bg-yellow-500 text-white rounded-lg text-sm font-medium flex items-center gap-1 hover:bg-yellow-600">
                <EyeOff className="w-3.5 h-3.5" /> Set Out of Stock
              </button>
              <select onChange={(e) => { if (e.target.value) handleBulkCategory(e.target.value); e.target.value = ''; }}
                className="px-3 py-1.5 bg-gray-600 text-white rounded-lg text-sm font-medium border-0 cursor-pointer"
                defaultValue="">
                <option value="" disabled>Change Category...</option>
                {categories.map(c => <option key={c} value={c}>{c?.replace(/_/g, ' ')}</option>)}
              </select>
              <button onClick={clearSelection}
                className="ml-auto px-3 py-1.5 text-blue-600 hover:text-blue-800 text-sm font-medium">
                Clear Selection
              </button>
            </div>
          )}

          {/* Delete All Button */}
          {wholesalerId && (
            <div className="mb-4">
              <button onClick={handleDeleteAll}
                className="px-4 py-2 bg-red-700 text-white rounded-lg text-sm font-medium flex items-center gap-2 hover:bg-red-800">
                <Trash2 className="w-4 h-4" /> Delete All Products for Selected Wholesaler
              </button>
            </div>
          )}

          {/* Results */}
          <div className="bg-white rounded-lg border" {...dragProps}>
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button onClick={toggleSelectAll} className="p-1 hover:bg-gray-100 rounded" title="Select all">
                  {selectedIds.size === products.length && products.length > 0 ? <CheckSquare className="w-4 h-4 text-blue-600" /> : <Square className="w-4 h-4 text-gray-400" />}
                </button>
                <span className="text-sm text-gray-500">{total} products found</span>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                  className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
                <span className="text-sm text-gray-600">Page {page} of {totalPages}</span>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                  className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
              </div>
            </div>

            {loading ? (
              <div className="p-8 flex justify-center"><LoadingSpinner /></div>
            ) : products.length === 0 ? (
              <div className="p-12 text-center">
                <Package className="w-12 h-12 mx-auto mb-3 text-gray-200" />
                <p className="text-gray-400">No products found</p>
              </div>
            ) : (
              <div className="divide-y">
                {products.map((p) => (
                  <div key={p._id} data-select-id={p._id}
                    className={`flex items-center gap-4 px-4 py-3 hover:bg-gray-50 group ${selectedIds.has(p._id) || dragSelectedIds.has(p._id) ? 'bg-blue-50' : ''}`}>
                    {/* Checkbox */}
                    <button onClick={(e) => handleItemClick(p._id, e)} className="flex-shrink-0">
                      {selectedIds.has(p._id) ? <CheckSquare className="w-4 h-4 text-blue-600" /> : <Square className="w-4 h-4 text-gray-300" />}
                    </button>
                    {/* Image */}
                    <div className="w-12 h-12 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                      {p.images?.[0] ? (
                        <img src={getImageUrl(p.images[0])} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Package className="w-5 h-5 text-gray-300" />
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{p.name}</p>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        <span className="text-xs text-blue-600 font-medium">{p.wholesalerId?.companyName || 'Unknown'}</span>
                        <span className="text-xs text-gray-300">•</span>
                        <span className="text-xs text-gray-400">{p.category?.replace(/_/g, ' ')}</span>
                        {p.sku && <>
                          <span className="text-xs text-gray-300">•</span>
                          <span className="text-xs text-gray-400">SKU: {p.sku}</span>
                        </>}
                      </div>
                    </div>

                    {/* Price */}
                    <div className="text-right hidden sm:block">
                      <p className="text-sm font-semibold text-gray-900">€{(p.pricing?.unitPrice || 0).toFixed(2)}</p>
                      <p className="text-xs text-gray-400">/{p.pricing?.unit || 'unit'}</p>
                    </div>

                    {/* Stock badge */}
                    <span className={`px-2 py-0.5 rounded text-xs font-medium hidden md:inline ${
                      p.stockStatus === 'out_of_stock' ? 'bg-red-100 text-red-700' :
                      p.stockStatus === 'low_stock' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-green-100 text-green-700'
                    }`}>
                      {p.stockStatus === 'out_of_stock' ? 'Out of Stock' : p.stockStatus === 'low_stock' ? 'Low Stock' : 'In Stock'}
                    </span>

                    {/* Images count */}
                    <span className="text-xs text-gray-400 hidden lg:flex items-center gap-1">
                      <Image className="w-3 h-3" /> {p.images?.length || 0}
                    </span>

                    {/* Actions */}
                    <div className="flex items-center gap-1">
                      <button onClick={() => setEditProduct(p)} title="Edit"
                        className="p-2 rounded-lg hover:bg-blue-50 text-gray-400 hover:text-blue-600 transition-colors">
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleToggleStock(p._id)} title="Toggle stock"
                        className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-amber-600 transition-colors">
                        {p.stockStatus === 'out_of_stock' ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>
                      <button onClick={() => handleDelete(p._id, p.name)} title="Delete"
                        className="p-2 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-600 transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Floating Bulk Action Bar */}
          <FloatingActionBar
            count={selectedIds.size}
            onClear={clearSelection}
            onSelectAll={selectedIds.size < products.length ? selectAll : undefined}
          >
            <button onClick={handleBulkDelete}
              className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium flex items-center gap-1">
              <Trash2 className="w-3.5 h-3.5" /> Delete
            </button>
            <button onClick={() => handleBulkStock(true)}
              className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium flex items-center gap-1">
              <Eye className="w-3.5 h-3.5" /> In Stock
            </button>
            <button onClick={() => handleBulkStock(false)}
              className="px-3 py-1.5 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg text-sm font-medium flex items-center gap-1">
              <EyeOff className="w-3.5 h-3.5" /> Out of Stock
            </button>
            <div className="relative">
              <select onChange={(e) => { if (e.target.value) handleBulkCategory(e.target.value); e.target.value = ''; }}
                className="px-3 py-1.5 bg-gray-600 hover:bg-gray-500 rounded-lg text-sm font-medium text-white border-0 cursor-pointer appearance-none pr-6"
                defaultValue="">
                <option value="" disabled>Category...</option>
                {categories.map(c => <option key={c} value={c}>{c?.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
          </FloatingActionBar>

          <DragSelectOverlay isDragging={isDragging} dragRect={dragRect} />
        </main>
      </div>
      {/* Edit Modal */}
      {editProduct && (
        <EditProductModal
          product={editProduct}
          onClose={() => setEditProduct(null)}
          onSave={() => { setEditProduct(null); fetchProducts(); fetchStats(); }}
        />
      )}
      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        icon={confirmModal.icon}
        confirmLabel={confirmModal.confirmLabel}
        confirmColor={confirmModal.confirmColor}
        onConfirm={confirmModal.onConfirm}
        onCancel={closeConfirm}
      />
    </div>
  );
};

export default AdminProducts;